import { ChevronDown } from "lucide-react";
import {
	forwardRef,
	useCallback,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { cn } from "@/utils";

export interface DropdownOption {
	value: string;
	label: string;
}

export interface DropdownOptionGroup {
	label: string;
	options: DropdownOption[];
}

interface OptionButtonProps {
	option: DropdownOption;
	flatIndex: number;
	listboxId: string;
	value: string;
	sizeClasses: string;
	highlightedIndex: number;
	onChange: (value: string) => void;
	onClose: () => void;
	renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
}

const OptionButton = ({
	option,
	flatIndex,
	listboxId,
	value,
	sizeClasses,
	highlightedIndex,
	onChange,
	onClose,
	renderOptionPrefix,
}: OptionButtonProps) => (
	<button
		key={option.value}
		id={`${listboxId}-option-${flatIndex}`}
		type="button"
		role="option"
		aria-selected={option.value === value}
		onClick={() => {
			onChange(option.value);
			onClose();
		}}
		className={cn(
			"w-full text-left hover:bg-bg-secondary flex items-center gap-2",
			sizeClasses,
			option.value === value
				? "bg-bg-secondary text-text-primary font-medium"
				: "text-text-secondary",
			highlightedIndex === flatIndex && "bg-bg-tertiary",
		)}
	>
		{renderOptionPrefix?.(option)}
		{option.label}
	</button>
);

interface OptionsListProps {
	groups?: DropdownOptionGroup[];
	options: DropdownOption[];
	listboxId: string;
	value: string;
	sizeClasses: string;
	highlightedIndex: number;
	onChange: (value: string) => void;
	onClose: () => void;
	renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
}

const OptionsList = ({
	groups,
	options,
	listboxId,
	value,
	sizeClasses,
	highlightedIndex,
	onChange,
	onClose,
	renderOptionPrefix,
}: OptionsListProps) => {
	if (groups) {
		let flatIndex = 0;
		return groups.map((group) => (
			<div key={group.label}>
				<div className="px-2 py-1 text-2xs font-medium text-text-tertiary bg-bg-secondary">
					{group.label}
				</div>
				{group.options.map((option) => {
					const idx = flatIndex++;
					return (
						<OptionButton
							key={option.value}
							option={option}
							flatIndex={idx}
							listboxId={listboxId}
							value={value}
							sizeClasses={sizeClasses}
							highlightedIndex={highlightedIndex}
							onChange={onChange}
							onClose={onClose}
							renderOptionPrefix={renderOptionPrefix}
						/>
					);
				})}
			</div>
		));
	}
	return options.map((option, index) => (
		<OptionButton
			key={option.value}
			option={option}
			flatIndex={index}
			listboxId={listboxId}
			value={value}
			sizeClasses={sizeClasses}
			highlightedIndex={highlightedIndex}
			onChange={onChange}
			onClose={onClose}
			renderOptionPrefix={renderOptionPrefix}
		/>
	));
};

interface DropdownProps
	extends Omit<React.ComponentPropsWithRef<"div">, "onChange"> {
	value: string;
	onChange: (value: string) => void;
	options: DropdownOption[];
	disabled?: boolean;
	placeholder?: string;
	/** Size variant: 'md' (default) matches existing Dropdown sizing, 'sm' matches compact filter panels. */
	size?: "sm" | "md";
	/** When provided, options are rendered grouped under section headers instead of flat. */
	groups?: DropdownOptionGroup[];
	/** Optional prefix element rendered before each option label in the dropdown list. */
	renderOptionPrefix?: (option: DropdownOption) => React.ReactNode;
	/** Optional prefix element rendered before the selected value display. */
	renderSelectedPrefix?: (option: DropdownOption) => React.ReactNode;
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
	function Dropdown(
		{
			value,
			onChange,
			options,
			disabled = false,
			placeholder = "Select...",
			className,
			size = "md",
			groups,
			renderOptionPrefix,
			renderSelectedPrefix,
			...props
		},
		ref,
	) {
		const [isOpen, setIsOpen] = useState(false);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const listboxId = useId();

		const closeDropdown = useCallback(() => setIsOpen(false), []);
		useClickOutside(dropdownRef, closeDropdown, isOpen);

		// Build a flat list of selectable options for keyboard navigation.
		// When groups are provided, the flat list is derived from groups (skipping group headers).
		const flatOptions = useMemo(() => {
			if (!groups) return options;
			return groups.flatMap((g) => g.options);
		}, [groups, options]);

		const handleSelect = useCallback(
			(index: number) => {
				onChange(flatOptions[index].value);
				setIsOpen(false);
			},
			[onChange, flatOptions],
		);

		const { highlightedIndex, getKeyboardProps } = useKeyboardNavigation({
			itemCount: flatOptions.length,
			isOpen,
			onSelect: handleSelect,
			onClose: closeDropdown,
		});

		const selectedOption =
			options.find((opt) => opt.value === value) ??
			(groups
				? groups.flatMap((g) => g.options).find((opt) => opt.value === value)
				: undefined);

		const activeDescendant =
			highlightedIndex >= 0
				? `${listboxId}-option-${highlightedIndex}`
				: undefined;

		// Size-based classes
		const isSm = size === "sm";
		const triggerSizeClasses = isSm ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm";
		const optionSizeClasses = isSm
			? "px-2 py-1.5 text-xs"
			: "px-3 py-2 text-sm";
		const chevronSize = isSm ? 12 : 14;
		const maxHeight = isSm ? "max-h-48" : "max-h-60";

		return (
			<div
				className={cn("relative", className)}
				ref={ref ?? dropdownRef}
				{...props}
			>
				<button
					type="button"
					onClick={() => !disabled && setIsOpen(!isOpen)}
					disabled={disabled}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-controls={listboxId}
					aria-activedescendant={activeDescendant}
					{...getKeyboardProps()}
					className={cn(
						"w-full text-left border border-border-secondary rounded-md focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent disabled:bg-bg-secondary disabled:text-text-tertiary flex items-center justify-between transition-colors",
						triggerSizeClasses,
						value
							? "bg-bg-secondary hover:bg-bg-secondary"
							: "bg-bg-primary hover:bg-bg-secondary",
					)}
				>
					<span
						className={cn(
							"flex items-center gap-2",
							value ? "text-text-primary" : "text-text-tertiary",
						)}
					>
						{selectedOption && renderSelectedPrefix?.(selectedOption)}
						{selectedOption?.label || placeholder}
					</span>
					<ChevronDown
						size={chevronSize}
						className={cn(
							"text-text-tertiary transition-transform duration-200",
							isOpen && "rotate-180",
						)}
					/>
				</button>

				{isOpen && !disabled && (
					<div
						id={listboxId}
						role="listbox"
						className={cn(
							"absolute z-popover w-full mt-1 bg-bg-primary border border-border-secondary rounded-md shadow-lg overflow-auto",
							maxHeight,
						)}
					>
						<OptionsList
							groups={groups}
							options={options}
							listboxId={listboxId}
							value={value}
							sizeClasses={optionSizeClasses}
							highlightedIndex={highlightedIndex}
							onChange={onChange}
							onClose={closeDropdown}
							renderOptionPrefix={renderOptionPrefix}
						/>
					</div>
				)}
			</div>
		);
	},
);

export default Dropdown;
